import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import {
  createMeetingMinutes,
  fetchMeetingMinutesById,
  fetchMeetingMinutesTemplates,
  updateMeetingMinutes,
} from "../../../api/meetingMinutes";
import { fetchUsers } from "../../../api/user";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { EmployeeCustomSelector } from "../../../components/utils/moduleSelectors/employeeCustomSelector";
import { PMCustomSelector } from "../../../components/utils/PMCustomSelector";
import ModernProjectSelector from "../../../components/utils/Selector/ModernProjectSelector";
import {
  MeetingMinutesShell,
  mmCardClass,
  mmPrimaryButtonClass,
  mmSecondaryButtonClass,
} from "./MeetingMinutesShell";
import { MeetingMinutesTypePills } from "./MeetingMinutesTypePills";
import { MeetingMinutesTemplatePicker } from "./MeetingMinutesTemplatePicker";

export const MeetingMinutesEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const templateIdFromUrl = searchParams.get("template_id");
  const copyFromId = searchParams.get("copy_from");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [selectedProjectObject, setSelectedProjectObject] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templateIdFromUrl ? parseInt(templateIdFromUrl, 10) : null
  );
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    meeting_type: "project_meeting",
    employee_id: null,
    project_id: null,
    participants: [],
    manual_participants_list: [],
  });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const [templatesResponse, usersResponse] = await Promise.all([
          fetchMeetingMinutesTemplates({ per_page: 100 }),
          fetchUsers({ per_page: 100 }),
        ]);
        const loadedTemplates = templatesResponse.data || [];
        setTemplates(loadedTemplates);
        setUsers(usersResponse.data?.data || usersResponse.data || []);

        if (isEdit) {
          const data = await fetchMeetingMinutesById(id);
          let manualParticipantsList = [];
          try {
            if (data.manual_participants) {
              manualParticipantsList = JSON.parse(data.manual_participants);
            }
          } catch (e) {
            manualParticipantsList = [];
          }
          setFormData({
            title: data.title || "",
            description: data.description || "",
            meeting_type: data.meeting_type || "other",
            employee_id: data.employee?.id || null,
            project_id: data.project?.id || null,
            participants: data.participants?.map((p) => p.id) || [],
            manual_participants_list: Array.isArray(manualParticipantsList)
              ? manualParticipantsList
              : [],
          });
          if (data.project?.id) setSelectedProjectObject(data.project);
        } else if (copyFromId) {
          const copyData = await fetchMeetingMinutesById(copyFromId);
          setFormData({
            title: `${copyData.title} (${t("meeting_minutes.copy_suffix")})`,
            description: copyData.description || "",
            meeting_type: copyData.meeting_type || "other",
            employee_id: copyData.employee?.id || null,
            project_id: copyData.project?.id || null,
            participants: copyData.participants?.map((p) => p.id) || [],
            manual_participants_list: [],
          });
          if (copyData.project?.id) setSelectedProjectObject(copyData.project);
        } else if (templateIdFromUrl) {
          const template = loadedTemplates.find(
            (row) => row.id === parseInt(templateIdFromUrl, 10)
          );
          if (template) applyTemplate(template, false);
        }
      } catch (err) {
        toast.error(t("meeting_minutes.load_error"));
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [id, templateIdFromUrl, copyFromId, isEdit, t]);

  const applyTemplate = (template, overwriteTitle = true) => {
    setSelectedTemplateId(template?.id || null);
    if (!template) return;
    setFormData((prev) => ({
      ...prev,
      meeting_type: template.meeting_type || prev.meeting_type,
      title: overwriteTitle && !prev.title ? template.title : prev.title,
      description: prev.description || template.description || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        meeting_date: new Date().toISOString().split("T")[0],
        manual_participants:
          formData.manual_participants_list?.length > 0
            ? JSON.stringify(formData.manual_participants_list)
            : "",
      };
      delete payload.manual_participants_list;

      if (isEdit) {
        const result = await updateMeetingMinutes({ ...payload, id });
        if (result?.errors) throw new Error(result.errors.join?.(", ") || "update");
        toast.success(t("meeting_minutes.updated"));
        navigate(`/dashboard/meeting-minutes/${id}`);
        return;
      }

      const result = await createMeetingMinutes(payload, selectedTemplateId);
      if (result?.errors) throw new Error(result.errors.join?.(", ") || "create");
      toast.success(t("meeting_minutes.created"));
      const firstMeeting = result?.meetings?.[0];
      if (result?.id && firstMeeting?.id) {
        navigate(`/dashboard/meeting-minutes/${result.id}/meetings/${firstMeeting.id}`);
      } else if (result?.id) {
        navigate(`/dashboard/meeting-minutes/${result.id}`);
      } else {
        navigate("/dashboard/meeting-minutes");
      }
    } catch (err) {
      toast.error(isEdit ? t("meeting_minutes.update_error") : t("meeting_minutes.create_error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ResourceLoading />;

  return (
    <MeetingMinutesShell
      eyebrow={isEdit ? t("meeting_minutes.edit") : t("meeting_minutes.new")}
      title={isEdit ? t("meeting_minutes.edit") : t("meeting_minutes.new")}
      subtitle={t("meeting_minutes.editor_subtitle")}
      backTo="/dashboard/meeting-minutes"
      backLabel={t("Back")}
    >
      <form onSubmit={handleSubmit} className={`${mmCardClass()} px-6 py-6`}>
        <div className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              {t("meeting_minutes.title")} *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              {t("meeting_minutes.meeting_type")} *
            </label>
            <MeetingMinutesTypePills
              value={formData.meeting_type}
              onChange={(meeting_type) => setFormData({ ...formData, meeting_type })}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              {t("meeting_minutes.description")}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
            />
          </div>

          {formData.meeting_type === "employee_interview" ? (
            <EmployeeCustomSelector
              label={t("meeting_minutes.select_employee")}
              selected={
                formData.employee_id
                  ? users.find((user) => user.id === formData.employee_id) || {
                      id: formData.employee_id,
                    }
                  : null
              }
              onChange={(employeeId) =>
                setFormData({ ...formData, employee_id: employeeId || null })
              }
            />
          ) : null}

          {formData.meeting_type === "project_meeting" ? (
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                {t("meeting_minutes.select_project")}
              </label>
              <button
                type="button"
                onClick={() => setShowProjectSelector(true)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm hover:bg-slate-50"
              >
                {selectedProjectObject
                  ? selectedProjectObject.displayTitle || selectedProjectObject.name
                  : t("meeting_minutes.select_project")}
              </button>
            </div>
          ) : null}

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              {t("meeting_minutes.select_participants")}
            </label>
            <PMCustomSelector
              name="participants"
              label={t("meeting_minutes.select_participants")}
              title={t("meeting_minutes.select_participants")}
              multiple={true}
              field="displayTitle"
              field2="email"
              field3="mobile"
              fetchFunction={async () => {
                const response = await fetchUsers({
                  filters: [{ name: "EMPLOYEE", value: true }],
                  per_page: 100,
                });
                return response?.data?.data || response?.data || response || [];
              }}
              selectedItems={formData.participants || []}
              onChange={(e) => {
                const selectedIds = Array.isArray(e.target.value)
                  ? e.target.value
                  : [e.target.value];
                setFormData({
                  ...formData,
                  participants: selectedIds.filter((participantId) => participantId),
                });
              }}
              values={{ participants: formData.participants }}
              setFieldValue={(name, value) => setFormData({ ...formData, [name]: value })}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-sm font-semibold text-slate-700">
                {t("meeting_minutes.employee.add_manual")}
              </label>
              <button
                type="button"
                onClick={() =>
                  setFormData({
                    ...formData,
                    manual_participants_list: [
                      ...formData.manual_participants_list,
                      { name: "", email: "", mobile: "" },
                    ],
                  })
                }
                className="text-sm font-semibold text-[#1463C7]"
              >
                <PlusIcon className="mr-1 inline h-4 w-4" />
                {t("meeting_minutes.employee.add_manual")}
              </button>
            </div>
            {formData.manual_participants_list.map((participant, index) => (
              <div key={index} className="mb-3 grid gap-3 rounded-2xl border border-slate-200 p-3 sm:grid-cols-3">
                <input
                  type="text"
                  value={participant.name}
                  placeholder={t("meeting_minutes.employee.manual_name")}
                  onChange={(e) => {
                    const next = [...formData.manual_participants_list];
                    next[index].name = e.target.value;
                    setFormData({ ...formData, manual_participants_list: next });
                  }}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                <input
                  type="email"
                  value={participant.email}
                  placeholder={t("meeting_minutes.employee.manual_email")}
                  onChange={(e) => {
                    const next = [...formData.manual_participants_list];
                    next[index].email = e.target.value;
                    setFormData({ ...formData, manual_participants_list: next });
                  }}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={participant.mobile}
                    placeholder={t("meeting_minutes.employee.manual_mobile")}
                    onChange={(e) => {
                      const next = [...formData.manual_participants_list];
                      next[index].mobile = e.target.value;
                      setFormData({ ...formData, manual_participants_list: next });
                    }}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        manual_participants_list: formData.manual_participants_list.filter(
                          (_, i) => i !== index
                        ),
                      })
                    }
                    className="text-red-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {!isEdit ? (
            <div>
              <label className="mb-3 block text-sm font-semibold text-slate-700">
                {t("meeting_minutes.templates.use_template")}
              </label>
              <MeetingMinutesTemplatePicker
                templates={templates}
                selectedId={selectedTemplateId}
                onSelect={(template) => applyTemplate(template)}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-500">{t("meeting_minutes.meetings_created_separately")}</p>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-6">
            <button
              type="button"
              onClick={() => navigate("/dashboard/meeting-minutes")}
              className={mmSecondaryButtonClass()}
            >
              {t("Cancel")}
            </button>
            <button type="submit" disabled={saving} className={mmPrimaryButtonClass()}>
              {saving ? t("Saving...") : isEdit ? t("Update") : t("Create")}
            </button>
          </div>
        </div>
      </form>

      {showProjectSelector ? (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setShowProjectSelector(false)}
            />
            <div className="relative flex h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
              <ModernProjectSelector
                open={showProjectSelector}
                selectedProjects={selectedProjectObject ? [selectedProjectObject] : []}
                onChange={(projects) => {
                  if (projects.length > 0) {
                    const project = projects[0];
                    setSelectedProjectObject(project);
                    setFormData({ ...formData, project_id: project.id });
                  } else {
                    setSelectedProjectObject(null);
                    setFormData({ ...formData, project_id: null });
                  }
                }}
                onClose={() => setShowProjectSelector(false)}
                multiple={false}
                showHeader={true}
                showSuggestedProjects={false}
              />
            </div>
          </div>
        </div>
      ) : null}
    </MeetingMinutesShell>
  );
};
